import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';

import { EvidenceDistribution } from './EvidenceDistribution';
import { EvidenceItemList } from './EvidenceItemList';
import {
  formatComputedAt,
  hasExclusions,
  isRescaled,
  overallWeight,
  prettifyTierKey,
  tierLabel,
  topLevers,
  weightPct,
} from './format';

import { cn } from '@/lib/cn';
import type {
  ReadinessBreakdown as Breakdown,
  ReadinessComponentEvidence,
  ReadinessComponentResult,
  ReadinessEvidence,
  ReadinessEvidenceItem,
  ReadinessFormula,
  ReadinessPillarResult,
} from '@/lib/api/readiness';


export interface ReadinessBreakdownProps {
  /** The scored breakdown from /lms/home or the org student detail payload. */
  breakdown?: Breakdown | null;
  /** The formula definition, for descriptions and tier labels. */
  formula?: ReadinessFormula | null;
  /** ISO-8601 timestamp of when the score was computed. */
  computedAt?: string | null;
  /** Section heading. */
  title?: string;
  className?: string;
  /**
   * The shape of each component's records plus a worst-first preview, keyed by
   * component key. Supplying this (or `onRequestEvidence` / `onViewAll`) is
   * what turns the component rows into disclosures; omit them all and the
   * render is exactly the student's read-only one.
   */
  evidence?: ReadinessEvidence | null;
  /**
   * Called once, when an interactive breakdown mounts, so the caller can own
   * the fetch. Asked for up front rather than on expand because the always-
   * visible distribution bar needs the counts — and the payload is now
   * constant-size, so there is nothing to defer.
   */
  onRequestEvidence?: () => void;
  /** True while that fetch is in flight. */
  evidenceLoading?: boolean;
  /**
   * Open the full, paged drill-down for one component. Without it a row stops
   * at its five-row preview — this component never renders a long list inline.
   */
  onViewAll?: (componentKey: string, componentLabel: string) => void;
  /**
   * Builds the href for an evidence item. Returns undefined for anything this
   * surface has no route for — the component never invents a link, because a
   * broken one is worse than plain text.
   */
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  /** Optional client-side navigation, so links don't cost a full page load. */
  onNavigate?: (href: string) => void;
}

/**
 * The one rendering of a readiness score's basis, shared by the student and
 * org/TPO views so the two can never disagree about why a number is what it
 * is. Every weight, label and tier name comes from the API — this file
 * contains no percentages of its own.
 *
 * For org callers it is also the drill-down: each scored component expands
 * into the records behind it, because a score a placement officer cannot
 * interrogate is a score they will not defend to a student. It stays
 * presentational — it fetches nothing and knows no routes; the caller supplies
 * both.
 */
export function ReadinessBreakdown({
  breakdown,
  formula,
  computedAt,
  title = 'What your score is made of',
  className,
  evidence,
  onRequestEvidence,
  evidenceLoading,
  onViewAll,
  linkFor,
  onNavigate,
}: ReadinessBreakdownProps) {
  const headingId = useId();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const requestedRef = useRef(false);

  const interactive = Boolean(onRequestEvidence || evidence || onViewAll);

  useEffect(() => {
    // One request serves every component and it is constant-size, so it is
    // asked for once on mount and every expand after that is free.
    if (!interactive || requestedRef.current) return;
    requestedRef.current = true;
    onRequestEvidence?.();
  }, [interactive, onRequestEvidence]);

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const collapseAll = useCallback(() => setExpanded({}), []);
  const openCount = useMemo(
    () => Object.values(expanded).filter(Boolean).length,
    [expanded],
  );

  if (!breakdown || !breakdown.has_data) {
    return (
      <section aria-labelledby={headingId} className={cn('text-left', className)}>
        <h3 id={headingId} className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        <p className="mt-2.5 text-[13.5px] font-semibold text-foreground">Not enough data yet</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          There is no graded work, attendance or curriculum progress to measure yet, so there is
          no readiness score to explain. A score appears as soon as any of it lands — it is not
          zero in the meantime.
        </p>
        <AsOf computedAt={computedAt} />
      </section>
    );
  }

  const excluded = hasExclusions(breakdown);
  const tier = tierLabel(breakdown.tier, formula) ?? prettifyTierKey(breakdown.tier);
  // Only shown to interactive (org) callers: it is an intervention hint for a
  // placement officer, not something to put in front of the student.
  const levers = interactive ? topLevers(breakdown) : new Map<string, number>();

  return (
    <section
      aria-labelledby={headingId}
      // The readable column is capped for the interrogable (org) render: at
      // full width the value ended up a screen's width from its label and the
      // row stopped reading as a pair. Two pillar columns inside this cap give
      // roughly a 500px measure each, which reads. The student's render is
      // untouched.
      className={cn('text-left', interactive && 'max-w-5xl', className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 id={headingId} className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {interactive && openCount > 0 && (
          <button
            type="button"
            onClick={collapseAll}
            className="flex-none rounded text-[10.5px] font-semibold text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Collapse all
          </button>
        )}
      </div>

      {/* Headline */}
      <div className="mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[28px] font-bold leading-none tabular-nums text-foreground">
          {Math.round(breakdown.score)}
        </span>
        <span className="text-xs font-medium text-muted-foreground">out of 100</span>
        {tier && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-foreground">
            {tier}
          </span>
        )}
      </div>
      {formula?.summary && (
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{formula.summary}</p>
      )}

      <ul
        className={cn(
          'mt-4',
          // Ability and Engagement sit side by side on the interrogable render:
          // they are two independent readings of the same student, and stacked
          // they pushed Engagement below the fold the moment anything expanded.
          // Below `lg` they stack, because two columns of drill-down at 390px
          // would be neither.
          interactive ? 'grid gap-x-8 gap-y-5 lg:grid-cols-2 lg:items-start' : 'space-y-4',
        )}
      >
        {breakdown.pillars.map((pillar) => (
          <PillarRow
            key={pillar.key}
            pillar={pillar}
            formula={formula}
            interactive={interactive}
            levers={levers}
            expanded={expanded}
            onToggle={toggle}
            evidence={evidence}
            evidenceLoading={evidenceLoading}
            onViewAll={onViewAll}
            linkFor={linkFor}
            onNavigate={onNavigate}
          />
        ))}
      </ul>

      <p className="mt-3.5 text-[11px] leading-relaxed text-muted-foreground">
        Based on {weightPct(breakdown.coverage)} of the formula
        {excluded ? ' — the rest has no data yet.' : '.'}
      </p>
      {excluded && formula?.renormalisation_note && (
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          {formula.renormalisation_note}
        </p>
      )}
      <AsOf computedAt={computedAt} />
    </section>
  );
}

/**
 * One shared grid for every component row, in both pillars.
 *
 * These were flex rows, so each cell sized to its own content: a row with a
 * "+25" lever chip pushed its bar and score left, a row without one did not,
 * and Ability and Engagement ended up with their columns in different places.
 * Side by side that reads as two unrelated tables rather than one comparison.
 *
 * Fixed tracks (label - weight - lever - bar - score) make every row line up
 * across the gutter, and the lever cell is reserved even when empty.
 */
const COMPONENT_ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_2.75rem_3.75rem_3rem_1.75rem] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_2.75rem_3.75rem_5rem_1.75rem] sm:gap-2.5';

/** Everything a component row needs beyond the score itself. */
interface EvidenceWiring {
  interactive: boolean;
  levers: Map<string, number>;
  expanded: Record<string, boolean>;
  onToggle: (key: string) => void;
  evidence?: ReadinessEvidence | null;
  evidenceLoading?: boolean;
  onViewAll?: (componentKey: string, componentLabel: string) => void;
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
}

function PillarRow({
  pillar,
  formula,
  ...wiring
}: {
  pillar: ReadinessPillarResult;
  formula?: ReadinessFormula | null;
} & EvidenceWiring) {
  const def = formula?.pillars.find((p) => p.key === pillar.key);
  const rescaled = !pillar.excluded && isRescaled(pillar.weight, pillar.effective_weight);

  return (
    <li>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-[13px] font-semibold text-foreground">{pillar.label}</span>
        {pillar.excluded ? (
          <span className="text-xs font-medium text-muted-foreground">No data yet</span>
        ) : (
          <span className="text-[13px] font-bold tabular-nums text-foreground">
            {Math.round(pillar.score ?? 0)}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
        {pillar.excluded ? (
          <>Nothing in this pillar has been measured yet, so it does not affect the score.</>
        ) : (
          <>
            {weightPct(pillar.effective_weight)} of the score
            {rescaled && <> (normally {weightPct(pillar.weight)})</>}
            {def?.description ? ` · ${def.description}` : ''}
          </>
        )}
      </p>

      <ul className="mt-2 space-y-2">
        {pillar.components.map((component) => (
          <ComponentRow
            key={component.key}
            component={component}
            pillar={pillar}
            description={
              def?.components.find((c) => c.key === component.key)?.description ?? ''
            }
            {...wiring}
          />
        ))}
      </ul>
    </li>
  );
}

function ComponentRow({
  component,
  pillar,
  description,
  interactive,
  levers,
  expanded,
  onToggle,
  evidence,
  evidenceLoading,
  onViewAll,
  linkFor,
  onNavigate,
}: {
  component: ReadinessComponentResult;
  pillar: ReadinessPillarResult;
  description: string;
} & EvidenceWiring) {
  const panelId = useId();
  const buttonId = useId();

  // `value === null` is "not measured yet", never a zero: rendering it as 0
  // would read as a failure the student never had. There is also nothing to
  // drill into, so these rows are never disclosures.
  if (component.excluded || component.value === null) {
    return (
      <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs">
        <span className="text-muted-foreground/70">{component.label}</span>
        <span className="text-[11px] italic text-muted-foreground/70">
          No data yet{component.excluded_reason && component.excluded_reason !== 'no data yet'
            ? ` — ${component.excluded_reason}`
            : ''}
        </span>
      </li>
    );
  }

  const value = Math.round(component.value);
  const share = overallWeight(pillar, component);
  const nominalShare = pillar.weight * component.weight;
  const rescaled = isRescaled(nominalShare, share);
  const lever = levers.get(component.key);
  const isOpen = !!expanded[component.key];

  const weightLine = (
    <p className="mt-0.5 text-[10.5px] text-muted-foreground/80">
      counts for {weightPct(share)} of the score
      {rescaled && <> (normally {weightPct(nominalShare)})</>}
    </p>
  );

  const bar = (
    <span className="h-[3px] w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <span
        className="block h-full rounded-full bg-foreground"
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </span>
  );

  if (!interactive) {
    return (
      <li>
        <div className={cn(COMPONENT_ROW_GRID, 'text-xs')}>
          <span className="min-w-0 truncate text-muted-foreground" title={description}>
            {component.label}
          </span>
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          {bar}
          <span className="text-right font-semibold tabular-nums text-foreground">{value}</span>
        </div>
        {weightLine}
      </li>
    );
  }

  const componentEvidence = evidence?.components?.[component.key];
  const counts = componentEvidence?.counts;

  return (
    <li>
      {/*
        L1 — constant height whatever the data does. The score, the weight and
        the distribution; never a row per record. A 327-topic curriculum and a
        3-challenge one occupy exactly the same space here.
      */}
      <button
        type="button"
        id={buttonId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => onToggle(component.key)}
        className={cn(
          COMPONENT_ROW_GRID,
          'w-full rounded-md px-1 py-1 text-left text-xs hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'h-3.5 w-3.5 flex-none text-muted-foreground transition-transform',
              isOpen && 'rotate-180',
            )}
          />
          <span className="min-w-0 truncate text-muted-foreground" title={description}>
            {component.label}
          </span>
        </span>
        {/*
          The weight rides in the header as a chip. As its own line under each
          of seven components, "counts for X% of the score" was seven near-
          identical sentences — noise at that repetition, however true.
        */}
        <span
          className="justify-self-end rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground"
          title={
            rescaled
              ? `Counts for ${weightPct(share)} of the score (normally ${weightPct(nominalShare)})`
              : `Counts for ${weightPct(share)} of the score`
          }
        >
          {weightPct(share)}
          <span className="sr-only">
            {` of the score${rescaled ? `, normally ${weightPct(nominalShare)}` : ''}`}
          </span>
          {rescaled && <span aria-hidden="true">*</span>}
        </span>
        {/*
          The lever cell is always rendered, empty or not. Ability carries these
          chips and Engagement often does not, so letting the cell collapse made
          the two pillars' bars and scores land in different places — the columns
          read as two unrelated tables rather than one comparison.
        */}
        {lever === undefined ? (
          <span aria-hidden="true" />
        ) : (
          <span
            className="justify-self-end rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-amber-700"
            title="Points recoverable if this component were fully closed"
          >
            +{lever} pts
          </span>
        )}
        {bar}
        <span className="text-right font-semibold tabular-nums text-foreground">{value}</span>
      </button>

      <EvidenceDistribution
        counts={counts}
        loading={!!evidenceLoading}
        className="ml-6 mr-1 mt-1"
      />

      {isOpen && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="mt-1.5 rounded-lg bg-muted/40 p-2.5"
        >
          <EvidencePanel
            componentKey={component.key}
            componentLabel={component.label}
            evidence={componentEvidence}
            loading={!!evidenceLoading}
            onViewAll={onViewAll}
            linkFor={linkFor}
            onNavigate={onNavigate}
          />
        </div>
      )}
    </li>
  );
}

/**
 * L2 — the expanded panel: the arithmetic in words and the few worst records.
 *
 * Never the full list. The previous version rendered everything the API sent
 * and, at 327 topics, produced a page nobody could read and a silent
 * truncation at 40 that hid the other 287. The honest version shows five and
 * hands the rest to the drawer.
 */
function EvidencePanel({
  componentKey,
  componentLabel,
  evidence,
  loading,
  onViewAll,
  linkFor,
  onNavigate,
}: {
  componentKey: string;
  componentLabel: string;
  evidence?: ReadinessComponentEvidence;
  loading: boolean;
  onViewAll?: (componentKey: string, componentLabel: string) => void;
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
}) {
  if (!evidence) {
    return loading ? (
      <p aria-busy="true" className="text-[11px] text-muted-foreground">
        Loading the detail…
      </p>
    ) : (
      <p className="text-[11px] text-muted-foreground">
        The detail behind this component couldn&apos;t be loaded.
      </p>
    );
  }

  const items = evidence.items ?? [];
  const total = evidence.counts?.total ?? items.length;

  return (
    <>
      <p className="text-[11px] font-medium text-foreground">{evidence.summary}</p>
      {items.length === 0 ? (
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Nothing has been recorded for this component yet.
        </p>
      ) : (
        <>
          {evidence.has_more && (
            <p className="mt-0.5 text-[10.5px] text-muted-foreground">
              The {items.length} costing the most, of {total}.
            </p>
          )}
          <EvidenceItemList
            items={items}
            linkFor={linkFor}
            onNavigate={onNavigate}
            className="mt-1.5"
          />
        </>
      )}
      {evidence.has_more && onViewAll && (
        <button
          type="button"
          onClick={() => onViewAll(componentKey, componentLabel)}
          className="mt-2 inline-flex items-center gap-1 rounded text-[11px] font-semibold text-primary underline underline-offset-2 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {`View all ${total}`}
          <ArrowRight aria-hidden="true" className="h-3 w-3" />
        </button>
      )}
    </>
  );
}

/** "as of" line — shown wherever a readiness score is, so a stale number is never mistaken for a live one. */
export function AsOf({ computedAt, className }: { computedAt?: string | null; className?: string }) {
  const text = formatComputedAt(computedAt);
  if (!text) return null;
  return (
    <p className={cn('mt-2 text-[11px] text-muted-foreground', className)}>{`as of ${text}`}</p>
  );
}
